import { useState } from 'react';

import { useEvent } from 'common/components/hooks/useEvent/useEvent';

import useLocalStorageChangeHandler from './useLocalStorageChangeHandler';
import { isNullOrUndefined } from './utils';

export type UseLocalStorageBackedStateInput<StateType> = {
  initialValue?: StateType;
  get: () => StateType;
  set: (value: StateType) => void;
};

/**
 * A hook similar to the `useState` hook, with the addition that it is synced with local storage,
 * meaning that:
 * 1. `setState` wrtes to local storage
 * 2. Changes in local storage are reflected in the hook's return value
 */
export default function useLocalStorageBackedState<StateType>({
  initialValue,
  set,
  get,
}: UseLocalStorageBackedStateInput<StateType>): [StateType, (value: StateType) => void] {
  const [internalState, setInternalState] = useState<StateType>(() => {
    if (!isNullOrUndefined(initialValue)) {
      set(initialValue);
    }

    return initialValue ?? get();
  });

  const onLocalStorageChange = useEvent(() => {
    const newValue = get();

    if (newValue !== internalState) {
      setInternalState(newValue);
    }
  });

  useLocalStorageChangeHandler({ onLocalStorageChange });

  const setState = useEvent((newState: StateType) => {
    set(newState);
    setInternalState(newState);
  });

  return [internalState, setState];
}
